import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";

const links = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about-us" },
  { label: "Solutions", href: "/solutions" },
  { label: "Blogs", href: "/blog" },
  { label: "Contact Us", href: "/contact-us" },
];

const solutionLinks = [
  { label: "EDC", href: "/electronic-data-capture-software-for-clinical-trials" },
  { label: "eConsent", href: "/electronic-consent-management-system" },
  { label: "ePRO", href: "/electronic-patient-reported-outcomes-software" },
  { label: "RTSM / IWRS", href: "/interactive-web-response-system" },
  { label: "TeleHealth", href: "/video-consultation-module-for-clinical-trials" },
  { label: "Participant Tracker", href: "/clinical-trial-participant-tracker-software" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-slate-950/70 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
        <a href="/" data-testid="nav-brand-logo" className="flex items-center gap-2.5 group">
          <span className="h-9 w-9 rounded-xl bg-white p-1 flex items-center justify-center shadow-[0_0_18px_-4px_rgba(16,185,129,0.5)]">
            <img src="/assets/etrialtrack-emblem.webp" alt="eTrialTrack logo" width="40" height="40" decoding="async" className="h-full w-full object-contain" />
          </span>
          <span className="font-display font-bold tracking-tight text-lg text-white">
            eTrial<span className="text-emerald-400">Track</span>
          </span>
        </a>
        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) =>
            l.label === "Solutions" ? (
              <div key={l.href} className="relative group">
                <a
                  href={l.href}
                  data-testid="nav-link-solutions"
                  className="flex items-center gap-1 text-sm text-slate-400 hover:text-white transition-colors duration-300"
                >
                  {l.label}
                  <ChevronDown className="w-3.5 h-3.5 transition-transform duration-300 group-hover:rotate-180" />
                </a>
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300">
                  <div className="glass-panel rounded-xl p-2 w-64 flex flex-col" data-testid="nav-solutions-dropdown">
                    {solutionLinks.map((s) => (
                      <a
                        key={s.href}
                        href={s.href}
                        data-testid={`nav-solution-${s.label.toLowerCase().replace(/[\s/]+/g, "-")}`}
                        className="px-4 py-2.5 rounded-lg text-sm text-slate-300 hover:text-white hover:bg-emerald-500/10 transition-colors duration-200"
                      >
                        {s.label}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm text-slate-400 hover:text-white transition-colors duration-300"
              >
                {l.label}
              </a>
            )
          )}
        </nav>
        <div className="hidden lg:flex items-center gap-4">
          <span className="font-mono-tech text-[10px] tracking-[0.15em] text-emerald-400/80 border border-emerald-500/25 rounded-full px-3 py-1.5">
            21 CFR PART 11 CERTIFIED
          </span>
          <a
            href="/#demo"
            data-testid="nav-schedule-demo-btn"
            className="group flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-sm font-semibold rounded-full px-5 py-2.5 transition-colors duration-300"
          >
            Schedule Demo
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-slate-300 hover:text-white"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden border-t border-white/5 bg-slate-950/95"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <div key={l.href} className="flex flex-col gap-3">
                  <a
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="text-base text-slate-300 hover:text-emerald-400 transition-colors"
                  >
                    {l.label}
                  </a>
                  {l.label === "Solutions" && (
                    <div className="pl-4 flex flex-col gap-2.5 border-l border-emerald-500/25">
                      {solutionLinks.map((s) => (
                        <a
                          key={s.href}
                          href={s.href}
                          onClick={() => setOpen(false)}
                          className="text-sm text-slate-500 hover:text-emerald-400 transition-colors"
                        >
                          {s.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <a
                href="/#demo"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-demo-btn"
                className="inline-flex justify-center bg-emerald-500 text-slate-950 font-semibold rounded-full px-5 py-3"
              >
                Schedule Demo
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
